import {
	IDocument,
	IEnvelopeCreatePayload,
	IEnvelopeData,
	ISignatario,
	TEnvelopeCreatePayload,
	TEnvelopeTotalCount,
} from "../../../../pages/signature/types/types";
import { ApiSignature } from "../axios-config";

const getEnvelope = async (
	page = 1,
	limit = 10,
	search = "",
	dataini = "",
	datafim = "",
	status = "",
	fieldfilter = "createdAt"
): Promise<TEnvelopeTotalCount | Error> => {
	try {
		const normalizedStatus = status === "todos" ? "" : status;
		const { data } = await ApiSignature().get(`/signature/envelopes/search`, {
			params: {
				_page: page,
				_limit: limit,
				name: search || undefined,
				status: normalizedStatus || undefined,
				dataini: dataini || undefined,
				datafim: datafim || undefined,
				fieldfilter: fieldfilter || undefined,
			},
		});
		if (data?.result && typeof data?.total === "number") {
			return { data: data.result as IEnvelopeData[], totalCount: data.total as number };
		}
		return new Error("Erro ao buscar envelopes");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar envelopes");
	}
};

const createEnvelope = async (payload: TEnvelopeCreatePayload): Promise<IEnvelopeCreatePayload | Error> => {
	try {
		const { data } = await ApiSignature().post(`/signature/envelopes`, payload);
		if (data) return data as IEnvelopeCreatePayload;
		return new Error("Erro ao criar envelope");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao criar envelope");
	}
};

const getEnvelopeByID = async (id: string): Promise<IEnvelopeData | Error> => {
	try {
		const { data } = await ApiSignature().get(`/signature/envelopes/${id}`);
		if (data) return data as IEnvelopeData;
		return new Error("Erro ao buscar envelope");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar envelope");
	}
};

const cancelEnvelope = async (id: string): Promise<void | Error> => {
	try {
		const { data } = await ApiSignature().delete(`/signature/envelopes/${id}/cancel`);
		if (!data) return new Promise((_, reject) => reject(new Error("Erro ao cancelar envelope.")));
		return data;
	} catch (error: any) {
		console.error(error);
		return new Promise((_, reject) => reject(error));
	}
};

const createDocument = async (envelopeId: string, file: File, filename: string): Promise<IDocument | Error> => {
	try {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("filename", filename);

		const { data } = await ApiSignature().post(`/signature/envelopes/${envelopeId}/documents`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		if (data) return data as IDocument;
		return new Error("Erro ao criar documento");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao criar documento");
	}
};

const getDocumentsByEnvelope = async (envelopeId: string): Promise<IDocument[] | Error> => {
	try {
		const { data } = await ApiSignature().get(`/signature/envelopes/${envelopeId}/documents`);
		if (Array.isArray(data)) return data as IDocument[];
		return new Error("Erro ao listar documentos");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao listar documentos");
	}
};

const downloadDocument = async (envelopeId: string, documentId: string): Promise<string | Error> => {
	try {
		const { data } = await ApiSignature().get(`/signature/envelopes/${envelopeId}/documents/${documentId}/download`);
		if (data?.url) return data.url;
		return new Error("Erro ao buscar link de download");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar link de download");
	}
};

// ✅ CORREÇÃO: Serviço para deletar um documento, agora tratando o retorno da API
const deleteDocument = async (envelopeId: string, documentId: string): Promise<void | Error> => {
	try {
		// A API de deleção geralmente retorna um status 204 (No Content) em caso de sucesso.
		// Se a chamada for bem-sucedida, não há problema, podemos retornar.
		await ApiSignature().delete(`/signature/envelopes/${envelopeId}/documents/${documentId}`);
		return;
	} catch (error: any) {
		console.error(error);
		// Retorna o erro como um objeto Error para que o hook o capture.
		return new Error(error?.response?.data?.message || "Erro ao deletar documento.");
	}
};

const createSignatario = async (
	envelopeId: string,
	payload: { name: string; email: string; document?: string; birthdate?: string; type?: "signer" }
): Promise<ISignatario | Error> => {
	try {
		const { data } = await ApiSignature().post(`/signature/envelopes/${envelopeId}/signatario`, payload);
		if (data) return data as ISignatario;
		return new Error("Erro ao criar signatário");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao criar signatário");
	}
};

const getSignatariosByEnvelope = async (envelopeId: string): Promise<ISignatario[] | Error> => {
	try {
		const { data } = await ApiSignature().get(`/signature/envelopes/${envelopeId}/signatarios`);
		if (Array.isArray(data)) return data as ISignatario[];
		return new Error("Erro ao listar signatários");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao listar signatários");
	}
};

const searchSignatarios = async (page = 1, limit = 10, search = ""): Promise<{ data: ISignatario[]; totalCount: number } | Error> => {
	try {
		const { data } = await ApiSignature().get(`/signature/signatarios/search`, {
			params: { _page: page, _limit: limit, search },
		});
		if (data?.data && typeof data?.total === "number") {
			return { data: data.data as ISignatario[], totalCount: data.total as number };
		}
		return new Error("Erro ao pesquisar signatários");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao pesquisar signatários");
	}
};

const getObservadoresByEnvelope = async (envelopeId: string): Promise<ISignatario[] | Error> => {
	try {
		const { data } = await ApiSignature().get(`/signature/envelopes/${envelopeId}/observador/`);
		if (Array.isArray(data)) return data as ISignatario[];
		return new Error("Erro ao listar observadores");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao listar observadores");
	}
};

const createObservador = async (
	envelopeId: string,
	payload: { name: string; email: string; document?: string; type?: "observers" }
): Promise<ISignatario | Error> => {
	try {
		const { data } = await ApiSignature().post(`/signature/envelopes/${envelopeId}/observador/`, payload);
		if (data) return data as ISignatario;
		return new Error("Erro ao criar observador");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao criar observador");
	}
};

const getObservadorByID = async (envelopeId: string, observadorId: string): Promise<ISignatario | Error> => {
	try {
		const { data } = await ApiSignature().get(`/signature/envelopes/${envelopeId}/observador/${observadorId}`);
		if (data) return data as ISignatario;
		return new Error("Erro ao buscar observador");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar observador");
	}
};

const deleteObservador = async (envelopeId: string, observadorId: string): Promise<void | Error> => {
	try {
		const { data } = await ApiSignature().delete(`/signature/envelopes/${envelopeId}/observador/${observadorId}`);
		if (!data) return new Promise((_, reject) => reject(new Error("Erro ao deletar observador.")));
		return data;
	} catch (error: any) {
		console.error(error);
		return new Promise((_, reject) => reject(error));
	}
};

const deleteSignatario = async (envelopeId: string, signatarioId: string): Promise<void | Error> => {
	try {
		const { data } = await ApiSignature().delete(`/signature/envelopes/${envelopeId}/signatario/${signatarioId}`);
		if (!data) return new Promise((_, reject) => reject(new Error("Erro ao deletar signatário.")));
		return data;
	} catch (error: any) {
		console.error(error);
		return new Promise((_, reject) => reject(error));
	}
};

const resendLinkToSignatario = async (envelopeId: string, signatarioId: string): Promise<void | Error> => {
	try {
		const { data } = await ApiSignature().post(`/signature/envelopes/${envelopeId}/signatario/${signatarioId}/resend-link`);
		if (!data) return new Promise((_, reject) => reject(new Error("Erro ao reenviar link.")));
		return data;
	} catch (error: any) {
		console.error(error);
		return new Promise((_, reject) => reject(error));
	}
};

const resendLinkToObservador = async (envelopeId: string, observadorId: string): Promise<void | Error> => {
	try {
		const { data } = await ApiSignature().post(`/signature/envelopes/${envelopeId}/observador/${observadorId}/resend-link`);
		if (!data) return new Promise((_, reject) => reject(new Error("Erro ao reenviar link.")));
		return data;
	} catch (error: any) {
		console.error(error);
		return new Promise((_, reject) => reject(error));
	}
};

const sendToSign = async (envelopeId: string): Promise<void | Error> => {
	try {
		// Passo 1: Verificar Requisitos (requirements)
		await ApiSignature().post(`/signature/envelopes/${envelopeId}/requirements`);

		// Passo 2: Configurar Autenticação (authentication)
		await ApiSignature().post(`/signature/envelopes/${envelopeId}/authentication`);

		// Passo 3: Ativar o Envelope (activate)
		await ApiSignature().post(`/signature/envelopes/${envelopeId}/activate`);

		// Retorna sucesso (void) se todas as chamadas forem bem-sucedidas
		return;
	} catch (error: any) {
		console.error("Erro ao enviar envelope para assinatura:", error);
		// Retorna um objeto Error com a mensagem da API ou uma mensagem padrão
		return new Error("Erro ao enviar envelope para assinatura.");
	}
};

export const SignatureServices = {
	// envelopes
	getEnvelope,
	createEnvelope,
	getEnvelopeByID,
	cancelEnvelope,
	sendToSign,

	// documentos
	createDocument,
	getDocumentsByEnvelope,
	downloadDocument,
	deleteDocument,

	// signatários
	createSignatario,
	getSignatariosByEnvelope,
	searchSignatarios,
	deleteSignatario,
	resendLinkToSignatario,

	// observadores
	getObservadoresByEnvelope,
	createObservador,
	getObservadorByID,
	deleteObservador,
	resendLinkToObservador,
};
